<template>
  <div>
    <h1>View User</h1>
    <div v-if="!user">
      Loading, please wait...
    </div>
    <div v-else>
      <div class="card">
        <div class="card-header">User information</div>
        <div class="card-body">
          <div v-if="showUserInfoChange">
            <b-row>
              <b-col cols="3">
                <BaseTextInput label="Email" v-model="newInfo.newEmail" :v="$v.newInfo.newEmail"/>
              </b-col>
              <b-col cols="3">
                <BaseTextInput label="Last Name" v-model="newInfo.newLastName" :v="$v.newInfo.newLastName"/>
              </b-col>
              <b-col cols="3">
                <BaseTextInput label="First Name" v-model="newInfo.newFirstName" :v="$v.newInfo.newFirstName"/>
              </b-col>
            </b-row>
            <hr/>
            <b-row>
              <b-col>
                <b-button variant="primary" @click="submit('rename', null, $route.params.id, newInfo.newFirstName, newInfo.newLastName, newInfo.newEmail)">Submit</b-button>
              </b-col>
            </b-row>
          </div>
          <div v-else>
            <b-row>
              <b-col cols="3">
                <BaseTextOutput label="Email" :value="user.email"/>
              </b-col>
              <b-col cols="3">
                <BaseTextOutput label="Last Name" :value="user.lastName"/>
              </b-col>
              <b-col cols="3">
                <BaseTextOutput label="First Name" :value="user.firstName"/>
              </b-col>
              <b-col cols="1">
                <BaseTextOutput label="Enabled?" :value="user.enabled?'Yes':'No'"/>
              </b-col>
              <b-col cols="1">
                <BaseTextOutput label="Email verified?" :value="user.emailVerified?'Yes':'No'"/>
              </b-col>
            </b-row>
          </div>
          <div v-if="showPasswordChange">
            <hr/>
            <b-row>
              <b-col>
                <BaseTextInput v-model="newPassword" label="New password" :v="$v.newPassword" tip="At least 10 characters long. At least 1 letter (a-z, A-Z). At least 1 digit (0-9). At least one special character (!@#$%^&*-)"/>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <b-button variant="primary" @click="submit('password', null, $route.params.id, null, null, null, newPassword)">Submit</b-button>
              </b-col>
            </b-row>
          </div>
          <hr/>
          <b-row>
            <b-col>
              <b-button class="mb-2 mr-1r" :variant="showUserInfoChange?'secondary':'primary'" @click="showUserInfoChange = !showUserInfoChange">{{showUserInfoChange?'Cancel info change':'Change user information'}}</b-button>
              <b-button class="mb-2 mr-1r" :variant="showPasswordChange?'secondary':'primary'" @click="showPasswordChange = !showPasswordChange">{{showPasswordChange?'Cancel password change':'Change password?'}}</b-button>
              <span id="enableButton" v-if="!user.enabled">
                <b-button class="mb-2 mr-1r"  variant="primary" @click="submit('reenable', null, $route.params.id)">Enable</b-button>
              </span>
              <span id="disableButton" v-else>
                <b-button class="mb-2 mr-1r" variant="danger" @click="submit('disable', null, $route.params.id)">Disable</b-button>
              </span>
              <b-button class="mb-2 mr-1r" v-if="!user.emailVerified" variant="primary" @click="submit('verifyEmail', null, $route.params.id)">Verify email</b-button>

              <b-tooltip v-if="!user.enabled" target="enableButton">This will enable the user regardless of any provider user assignments.</b-tooltip>
              <b-tooltip v-if="user.enabled" target="disableButton">This will disable the user regardless of any provider user assignments.</b-tooltip>

            </b-col>
          </b-row>

        </div>
      </div>
      <div class="card">
        <div class="card-header">Role Information</div>
        <div class="card-body">
          <strong>Roles Assigned:</strong>
          <ul v-if="user.roles.length>0">
            <li v-for="(role, index) in user.roles" :key="index">{{role}}</li>
          </ul>
          <p v-else>No roles found</p>
        </div>
      </div>
      <div class="card">
        <div class="card-header">Certificate</div>
        <div v-if="user.certificate">
          <div class="card-body">
            <b-row>
              <b-col cols="4">
                <BaseTextOutput label="Number" :value="user.certificate.number"/>
              </b-col>
              <b-col cols="4">
                <BaseTextOutput label="Status" :value="user.certificate.status"/>
              </b-col>
              <b-col cols="4">
                <BaseTextOutput label="Expiration Date" :value="user.certificate.expireDate"/>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="4">
                <BaseTextOutput label="Initial registry date" :value="user.certificate.initialRegistryDate"/>
              </b-col>
              <b-col cols="4">
                <BaseTextOutput label="Skill test date" :value="user.certificate.skillTestDate"/>
              </b-col>
              <b-col cols="4">
                <BaseTextOutput label="Knowledge test date" :value="user.certificate.knowledgeTestDate"/>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="4">
                <BaseTextOutput label="Approved Date" :value="user.certificate.approvedDate"/>
              </b-col>
              <b-col cols="4">
                <BaseTextOutput label="Complaint status" :value="user.certificate.complaint"/>
              </b-col>
              <b-col cols="4">
                <BaseTextOutput label="Test-out" :value="user.certificate.testOut"/>
              </b-col>
            </b-row>
          </div>
          <div class="card-header" @click="showApplications = !showApplications">Applications <b-icon :icon="showApplications?'chevron-up':'chevron-down'"></b-icon></div>
          <div class="card-body" v-if="showApplications">
            <b-table
                :items="user.certificate.applications"
                hover small show-empty class="sr-caption"
                aria-live="polite"
                responsive="true"
                :fields="applicationFields"
            >
              <template v-slot:empty="scope">
                <p>No applications found.</p>
              </template>
              <template v-slot:cell(type)="row">{{row.item.type.description}}</template>
              <template v-slot:cell(status)="row">{{row.item.status.description}}</template>
              <template v-slot:cell(actions)="row"><a class="btn btn-primary sm" :href="(row.item.type.code==='I'?'/review-endorsement/':'/review-renewal/')+row.item.id" target="_blank">View</a></template>
            </b-table>
          </div>
          <div class="card-header" @click="showChangeRequests = !showChangeRequests">Change requests <b-icon :icon="showChangeRequests?'chevron-up':'chevron-down'"/></div>
          <div class="card-body" v-if="showChangeRequests">
            <h3>Name changes</h3>
            <b-table
                :items="user.certificate.nameChangeRequests"
                hover small show-empty class="sr-caption"
                aria-live="polite"
                responsive="true"
                :fields="nameChangeFields"
            >
              <template v-slot:empty="scope">
                <p>No name changes found.</p>
              </template>
              <template v-slot:cell(actions)="row">
                <b-button class="sm" variant="primary" target="_blank" :href="'/review-change/name/'+row.item.id+'/'+row.item.profileId">
                  View
                </b-button>
              </template>
            </b-table>
            <hr/>
            <h3>Date of birth changes</h3>
            <b-table
                :items="user.certificate.dobChangeRequests"
                hover small show-empty class="sr-caption"
                aria-live="polite"
                responsive="true"
                :fields="dobChangeFields"
            >
              <template v-slot:empty="scope">
                <p>No date of birth changes found.</p>
              </template>
              <template v-slot:cell(actions)="row">
                <b-button class="sm" variant="primary" target="_blank" :href="'/review-change/dob/'+row.item.id+'/'+row.item.profileId">
                  View
                </b-button>
              </template>
            </b-table>
            <hr/>
            <h3>SSN changes</h3>
            <b-table
                :items="user.certificate.ssnChangeRequests"
                hover small show-empty class="sr-caption"
                aria-live="polite"
                responsive="true"
                :fields="ssnChangeFields"
            >
              <template v-slot:empty="scope">
                <p>No social security number changes found.</p>
              </template>
              <template v-slot:cell(actions)="row">
                <b-button class="sm" variant="primary" target="_blank" :href="'/review-change/ssn/'+row.item.id+'/'+row.item.profileId">
                  View
                </b-button>
              </template>
            </b-table>
          </div>
          <div class="card-footer">
            <b-row>
              <b-col>
                <b-button variant="danger" class="mr-1r" @click="submit('unlink', null, $route.params.id)">Unlink certificate</b-button>
              </b-col>
            </b-row>
          </div>
        </div>
        <div v-else>
          <div class="card-body">
            <p>No certificate bound to this user</p>
            <b-row>
              <b-col>
                <b-button :disabled="user.roles.length>0" :variant="showCertLink?'secondary':'primary'" @click="showCertLink = !showCertLink">{{showCertLink?'Cancel':'Link to certificate'}}</b-button>
              </b-col>
            </b-row>
            <div v-if="showCertLink">
              <b-row>
                <b-col>
                  <BaseMaskedTextInput mask="########" v-model="newCertNumber" label="Certificate Number" :v="$v.newCertNumber" tip="8 digits"/>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <b-button :disabled="user.roles.length>0" variant="primary" @click="submit('link', null, $route.params.id, null, null, null, null, newCertNumber)">Link certificate</b-button>
                </b-col>
              </b-row>
            </div>
          </div>
        </div>
      </div>
      <div class="card">
        <div class="card-header">Provider User</div>
        <div class="card-body">
          <div v-if="user.providerUser">
            <b-row>
              <b-col cols="4">
                <BaseTextOutput label="Provider Name" :value="user.providerUser.name"/>
              </b-col>
              <b-col cols="4">
                <BaseTextOutput label="Provider HFID" :value="user.providerUser.hfid"/>
              </b-col>
              <b-col cols="4">
                <BaseTextOutput label="Provider Type" :value="user.providerUser.type"/>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="4">
                <BaseTextOutput label="Street Address" :value="user.providerUser.address"/>
              </b-col>
              <b-col cols="4">
                <BaseTextOutput label="City" :value="user.providerUser.city"/>
              </b-col>
              <b-col cols="4">
                <BaseTextOutput label="ZipCode" :value="user.providerUser.zipCode"/>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <p>To promote/demote/delete user visit the provider administration page: </p>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <a class="btn btn-primary" :href="'/viewFacility/'+user.providerUser.id" target="_blank">View provider</a>
              </b-col>
            </b-row>
          </div>
          <div v-else>
            <p>Not a user at any provider. To add as a provider user visit the facility management page. </p>

          </div>
        </div>
      </div>
      <div class="card">
        <div class="card-header">Provider Admin</div>
        <div class="card-body">
          <div v-if="user.providerAdmin">
            <b-row>
              <b-col cols="4">
                <BaseTextOutput label="Provider Name" :value="user.providerAdmin.name"/>
              </b-col>
              <b-col cols="4">
                <BaseTextOutput label="Provider HFID" :value="user.providerAdmin.hfid"/>
              </b-col>
              <b-col cols="4">
                <BaseTextOutput label="Provider Name" :value="user.providerAdmin.type"/>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="4">
                <BaseTextOutput label="Street Address" :value="user.providerAdmin.address"/>
              </b-col>
              <b-col cols="4">
                <BaseTextOutput label="City" :value="user.providerAdmin.city"/>
              </b-col>
              <b-col cols="4">
                <BaseTextOutput label="ZipCode" :value="user.providerAdmin.zipCode"/>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <p>To promote/demote/delete user visit the provider administration page: </p>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <a class="btn btn-primary" :href="'/viewFacility/'+user.providerAdmin.id" target="_blank">View provider</a>
              </b-col>
            </b-row>
          </div>
          <div v-else>Not an admin at any provider. To add as a provider admin visit the facility management page.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseTextInput from "@/components/templates/input/BaseTextInput.vue";
import BaseTextOutput from "@/components/templates/input/BaseTextOutput.vue";
import axios from "axios";
import {email, helpers, minLength, numeric, required} from "vuelidate/lib/validators";
import BaseMaskedTextInput from "@/components/templates/input/BaseMaskedTextInput.vue";

// const passwordRegex = ;

const regexValidation = (obj) => /^(?=.*\d)(?=.*[A-Z])(?=.*[a-z])(?=.*[^\w\d\s:])([^\s]){10,}$/.test(obj);

export default {
  name: "ViewUser",
  components: {BaseMaskedTextInput, BaseTextInput, BaseTextOutput},
  data: ()=>({
    user: null,
    newInfo: {
      newEmail: null,
      newFirstName: null,
      newLastName: null,
    },
    newPassword: null,
    roles: [],
    showUserInfoChange: false,
    showPasswordChange: false,
    showApplications: false,
    showChangeRequests: false,
    showCertLink: false,
    newCertNumber: null,
    applicationFields: [
      {key: 'type', label: 'Type', sortable: true},
      {key: 'status', label: 'Status', sortable: true},
      {key: 'submittedDate', label: 'Submitted Date', sortable: true},
      {key: 'approvedDate', label: 'Approved Date', sortable: true},
      {key: 'expirationDate', label: 'Expiration Date', sortable: true},
      {key: 'actions', label: 'Actions'}
    ],
    nameChangeFields:[
      {key: 'lastName', label: 'Last name', sortable: true},
      {key: 'firstName', label: 'First name', sortable: true},
      {key: 'middleName', label: 'Middle name', sortable: true},
      {key: 'changeDate', label: 'Change date', sortable: true},
      {key: 'status', label: 'Status', sortable: true},
      {key: 'actions', label: 'Actions'}
    ],
    dobChangeFields:[
      {key: 'dateOfBirth', label: 'Date of birth', sortable: true},
      {key: 'changeDate', label: 'Change date', sortable: true},
      {key: 'status', label: 'Status', sortable: true},
      {key: 'actions', label: 'Actions'}
    ],
    ssnChangeFields:[
      {key: 'ssn', label: 'SSN', sortable: true},
      {key: 'changeDate', label: 'Change date', sortable: true},
      {key: 'status', label: 'Status', sortable: true},
      {key: 'actions', label: 'Actions'}
    ],

  }),
  methods: {
    loadUser(){
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/admin/user/${this.$route.params.id}`)
          .then(response=>{
            this.user = response.data;
            this.newInfo.newEmail = response.data.email;
            this.newInfo.newFirstName = response.data.firstName;
            this.newInfo.newLastName = response.data.lastName;
          });
    },
    submit(action, providerId, userId, firstName, lastName, email, newPassword, certNumber){
      switch(action){
        case "rename":
          this.$v.newInfo.$touch();
          if(this.$v.newInfo.$error){
            this.showRootToast('Missing or invalid name/email fields', 'Some of the name or email fields are invalid', 'warning');
            return;
          }
          break;
        case "password":
          this.$v.newPassword.$touch();
          if(this.$v.newPassword.$error){
            this.showRootToast('Invalid password', 'Password did not meet validation requirements', 'warning');
            return;
          }
          break;
        case "link":
          this.$v.newCertNumber.$touch();
          if(this.$v.newCertNumber.$error){
            this.showRootToast('Missing or invalid certificate number', 'Certificate number is missing or invalid', 'warning');
            return;
          }
          break;
      }
      let payload = {
        action: action,
        providerId: providerId,
        userId: userId,
        userFirstName: firstName,
        userLastName: lastName,
        userEmail: email,
        newPassword: newPassword,
        certNumber: certNumber
      };
      axios.post(`${process.env.VUE_APP_API_BASE_URL}/admin/user`, payload)
          .then(response => {
            this.$nextTick(()=>{
              switch(action){
                case "add":
                  this.showRootToast("User added", "User has been successfully added to this facility", "success");
                  break;
                case "addAdmin":
                  this.showRootToast("Admin added", "Admin has been successfully added to this facility", "success");
                  break;
                case "promote":
                  this.showRootToast("User promoted", "User has been successfully promoted to administrator", "success");
                  break;
                case "demote":
                  this.showRootToast("Admin demoted", "Admin has been successfully demoted to user", "success");
                  break;
                case "activate":
                  this.showRootToast("User reactivated", "User has been successfully reactivated", "success");
                  break;
                case "deactivate":
                  this.showRootToast("User deactivated", "User has been successfully deactivated", "success");
                  break;
                case "delete":
                  this.showRootToast("User deleted", "User has been successfully deleted from this facility", "success");
                  break;
                case "link":
                  this.showRootToast("User linked", "User has been successfully linked to a certificate", "success");
                  break;
                case "unlink":
                  this.showRootToast("User unlinked", "User has been successfully unlinked from a certificate", "success");
                  break;
                case "rename":
                  this.showRootToast("User information changed", "User's name and/or email has been successfully changed", "success");
                  break;
                case "password":
                  this.showRootToast("User password changed", "User's password has been successfully changed. They will be prompted to change it on first login.", "success");
                  break;
                case "reenable":
                  this.showRootToast('User re-enabled', 'User has been successfully re-enabled', 'success');
                  break;
                case "disable":
                  this.showRootToast('User disabled', 'User has been successfully disabled', 'success');
                  break;
                case "verifyEmail":
                  this.showRootToast('Email verified', 'User\'s email has been successfully marked as verified.', 'success');
                  break;
              }
              this.loadUser();
            })
          })
          .catch(error=>{
            let header;
            let body;
            switch(action){
              case "add":
                header = "Error adding user";
                break;
              case "addAdmin":
                header = "Error adding admin";
                break;
              case "promote":
                header = "Error promoting user";
                break;
              case "demote":
                header = "Error demoting user";
                break;
              case "activate":
                header = "Error reactivating user";
                break;
              case "deactivate":
                header = "Error deactivating user";
                break;
              case "delete":
                header = "Error deleting user";
                break;
              case "link":
                header = "Error linking certificate";
                break;
              case "unlink":
                header = "Error unlinking certificate";
                break;
              case "rename":
                header = "Error changing name/email";
                break;
              case "password":
                header = "Error changing password";
                break;
              case "reenable":
                header = 'Error re-enabling user';
                break;
              case "disable":
                header = 'Error disabling user';
                break;
              case "verifyEmail":
                header = 'Error verifying email';
                break;
              default:
                header = "Incorrect operation error";
            }
            switch(error.response.status){
              case 404:
                if(error.response.data.message==='provider'){
                  body="Provider not found";
                }
                else if(error.response.data.message==='user'){
                  body='User not found';
                }
                else if(error.response.data.message==='certificate'){
                  body='Certificate not found';
                }
                else{
                  body='Server returned 404. Possibly user account being referenced was deleted?'
                }
                break;
              case 406:
                body = "Certificate is already bound to user account id " + error.response.data.message;
                console.log(error.response);
                // window.open('/viewUser/'+error.response.data.message, "_blank");
                break;
              case 409:
                body="User already has a role "+error.response.data.message;
                break;
              default:
                body="An expected error occured, server returned code "+error.response.status+"and body: "+JSON.stringify(error.response.data.message);
            }
            this.$nextTick(()=>{this.showRootToast(header,body,'danger')});
          })
    },


    showRootToast(title, message, type) {
      this.$root.$bvToast.toast(message, {
        title: title,
        toaster: 'b-toaster-top-center',
        solid: true,
        variant: type,
        appendToast: true,
        noAutoHide:true
      })
      this.toastFocus()
    },
    toastFocus() {
      setTimeout(() => {
        let elem = document.getElementsByClassName("toast")
        elem = elem[elem.length-1]
        elem.focus()
      }, 100)
    },
  },
  validations(){
    return {
      newCertNumber: {
        required,
        numeric,
        minLength: minLength(8)
      },
      newInfo: {
        newEmail: {required, email},
        newLastName: {required},
        newFirstName: {required}
      },
      newPassword:{
        required,
        regexValidation
      },
    }
  },
  created(){
    this.loadUser();
  }
}
</script>

<style scoped>

</style>